/**
 * Whitelist of allowed search parameters for the search page.
 * Any parameters not in this list will be filtered out to prevent issues
 * with tracking parameters (gclid, utm_*, etc.) from breaking the search functionality.
 */

// Core listing type filters
export const LISTING_TYPE_PARAMS = [
  'preConstruction',
  'newConstruction',
  'forRent',
  'forSale',
  'foreclosures',
  'shortSales',
  'existingCondos',
  'comingSoon',
  'sold',
] as const;

// Price and size filters
export const RANGE_PARAMS = [
  'anyPrice',
  'anyBeds',
  'sqft',
  'yearBuild',
  'floor',
  'priceSqFt',
  'unitsInBuilding',
] as const;

// Amenity and feature filters
export const AMENITY_PARAMS = [
  'baths',
  'parkingPlaces',
  'petFriendly',
  'bitcoin',
  'sellerFinancing',
  'shortTermRent',
  'notShortTermRent',
  'furnished',
  'unfurnished',
  'intracoastalView',
  'oceanView',
  'skylineView',
  'beachfront',
  'oceanfront',
  'wfAny',
  'penthouse',
  'corner',
  'showPendingListings',
  'maxHOA',
  'rentalInvestments',
  'lowDownPayment',
  'shortTermRentTerms',
  'notShortTermRentTerms',
] as const;

// Search box location filters
export const LOCATION_PARAMS = [
  'cities',
  'counties',
  'neighborhoods',
  'buildings',
  'addresses',
  'zipCodes',
  'searchBoxFilters',
] as const;

// Map and coordinate filters
export const MAP_PARAMS = [
  'coordinates',
  'polygons',
  'poly',
  'sw',
  'ne',
  'z',
] as const;

// Other search filters
export const OTHER_SEARCH_PARAMS = [
  'keyWords',
  'daysOnCbb',
  'closedAt',
  'unitNumberRegex',
  'secretSearch',
  'sortOption',
  'propertyTypeCode',
  'page',
  'isLink',
] as const;

// Internal tracking params (allowed but not sent to server)
export const INTERNAL_TRACKING_PARAMS = ['ppc', 'att'] as const;

/**
 * Complete list of all allowed search parameters.
 * This is used to filter out unknown parameters from URLs.
 */
export const ALLOWED_SEARCH_PARAMS = [
  ...LISTING_TYPE_PARAMS,
  ...RANGE_PARAMS,
  ...AMENITY_PARAMS,
  ...LOCATION_PARAMS,
  ...MAP_PARAMS,
  ...OTHER_SEARCH_PARAMS,
  ...INTERNAL_TRACKING_PARAMS,
] as const;

/**
 * Parameters that should be omitted when sending to the server
 */
export const PARAMS_TO_OMIT_FOR_SERVER = [
  'sw',
  'ne',
  'z',
  'poly',
  'isLink',
  'page',
  'ppc',
  'att',
] as const;

/**
 * Common tracking parameters from various sources that should be ignored
 * (Google Ads, Facebook, UTM tracking, etc.)
 */
export const COMMON_TRACKING_PARAMS = [
  'gclid', // Google Ads click ID
  'fbclid', // Facebook click ID
  'msclkid', // Microsoft Ads click ID
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'mc_cid', // MailChimp campaign ID
  'mc_eid', // MailChimp email ID
  '_ga', // Google Analytics
  'ref',
  'referrer',
  'source',
  'campaign',
] as const;

export type AllowedSearchParam = typeof ALLOWED_SEARCH_PARAMS[number];
